import apiClient from '@/lib/api-client';
import type {
  UserInvitation,
  InvitationCreateRequest,
  InvitationValidationResponse,
  InvitationAcceptRequest,
  InvitationAcceptResponse
} from '@/types/invitation';

class InvitationService {
  /**
   * Get all invitations for the current organization
   */
  async getInvitations(status?: string): Promise<UserInvitation[]> {
    const params = status ? { status } : {};
    const { data: response } = await apiClient.get<{ data: any[] }>('/v1/admin/invitations', { params });

    return response.data.map(this.transformInvitation);
  }

  /**
   * Create a new invitation
   */
  async createInvitation(data: InvitationCreateRequest): Promise<UserInvitation> {
    const { data: response } = await apiClient.post<{ data: any }>('/v1/admin/invitations', {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      role: data.role,
      is_provider: data.isProvider || false,
      service_ids: data.serviceIds || [],
      personal_message: data.personalMessage
    });

    return this.transformInvitation(response.data);
  }

  /**
   * Resend an invitation
   */
  async resendInvitation(id: number): Promise<UserInvitation> {
    const { data: response } = await apiClient.post<{ data: any }>(`/v1/admin/invitations/${id}/resend`);

    return this.transformInvitation(response.data);
  }

  /**
   * Revoke an invitation
   */
  async revokeInvitation(id: number): Promise<{ message: string }> {
    const { data: response } = await apiClient.post<{ data: any }>(`/v1/admin/invitations/${id}/revoke`);

    return { message: response.data.message };
  }

  /**
   * Delete an invitation
   */
  async deleteInvitation(id: number): Promise<{ message: string }> {
    const { data: response } = await apiClient.delete<{ data: any }>(`/v1/admin/invitations/${id}`);

    return { message: response.data.message };
  }

  /**
   * Validate invitation token (public endpoint)
   */
  async validateInvitation(token: string): Promise<InvitationValidationResponse> {
    try {
      const { data: response } = await apiClient.get<{ data: any }>(`/v1/invitations/${token}/validate`);

      return {
        valid: response.data.valid,
        invitation: response.data.invitation ? {
          organizationName: response.data.invitation.organization_name,
          role: response.data.invitation.role,
          invitedBy: response.data.invitation.invited_by,
          firstName: response.data.invitation.first_name,
          lastName: response.data.invitation.last_name,
          email: response.data.invitation.email
        } : undefined,
        userExists: response.data.user_exists
      };
    } catch (error: any) {
      return {
        valid: false,
        message: error.response?.data?.message || 'Invalid or expired invitation'
      };
    }
  }

  /**
   * Accept invitation (public endpoint)
   */
  async acceptInvitation(data: InvitationAcceptRequest): Promise<InvitationAcceptResponse> {
    const { data: response } = await apiClient.post<{ data: any }>(`/v1/invitations/${data.token}/accept`, {
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone,
      password: data.password
    });

    return {
      user: {
        id: response.data.user.id,
        email: response.data.user.email,
        firstName: response.data.user.first_name,
        lastName: response.data.user.last_name,
        role: response.data.user.role,
        organizationId: response.data.user.organization_id
      },
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
      message: response.data.message
    };
  }

  /**
   * Transform backend invitation response to frontend type
   */
  private transformInvitation(apiInvitation: any): UserInvitation {
    return {
      id: apiInvitation.id,
      email: apiInvitation.email,
      firstName: apiInvitation.first_name,
      lastName: apiInvitation.last_name,
      role: apiInvitation.role,
      isProvider: apiInvitation.is_provider || false,
      status: apiInvitation.status,
      invitedBy: {
        id: apiInvitation.invited_by.id,
        name: apiInvitation.invited_by.name
      },
      personalMessage: apiInvitation.personal_message,
      sendCount: apiInvitation.send_count,
      lastSentAt: apiInvitation.last_sent_at,
      expiresAt: apiInvitation.expires_at,
      createdAt: apiInvitation.created_at,
      acceptedAt: apiInvitation.accepted_at
    };
  }
}

export const invitationService = new InvitationService();
